import React, { useMemo } from "react";
import { useGetCustomerEngagementQuery } from "../../../app/api/console/consoleCustomerAnalyticsApiSlice";
import { Card, CardHeader, EmptyState, Loading, Reading, StatCard, formatDuration } from "./ui";
import { rangeToIso } from "./helpers";

const pct = (v) => (v == null ? "—" : `${Math.round(v * 100)}%`);
const SURFACE_LABELS = {
  overview: "Overview (top 10)",
  findings: "Findings list",
  finding_group: "Finding group",
  finding: "Finding detail",
  activity: "Activity feed",
  run: "Run page",
  agents: "Agents",
  settings: "Settings",
  other: "Other",
};
const ENTRY_LABELS = { overview: "Ranked overview", results_page: "Findings list", activity_feed: "Activity feed", direct_link: "Direct link", share_link: "Shared link" };
const ENTRY_COLORS = { overview: "#8B5CF6", results_page: "#FF7B43", activity_feed: "#EC4899", direct_link: "#6C7187", share_link: "#0EA5E9" };
const FUNNEL_COLORS = ["#8B5CF6", "#7C3AED", "#6D28D9", "#1FAF5C"];

function FunnelRows({ funnel }) {
  const top = funnel[0] ? funnel[0].sessions : 0;
  return (
    <div className='space-y-3 my-2' data-testid='funnel'>
      {funnel.map((step, i) => {
        const prev = i > 0 ? funnel[i - 1].sessions : null;
        const width = top ? Math.max(2, Math.round((step.sessions / top) * 100)) : 0;
        return (
          <div key={step.step} className='grid grid-cols-[minmax(0,180px)_1fr_auto] items-center gap-3'>
            <div className='text-body-xs font-semibold text-text-primary truncate' title={step.step}>
              {step.step}
            </div>
            <div className='h-6 bg-surface-neutral rounded-lg overflow-hidden'>
              <div className='h-full rounded-lg' style={{ width: `${width}%`, backgroundColor: FUNNEL_COLORS[i] || FUNNEL_COLORS[0] }} />
            </div>
            <div className='text-right whitespace-nowrap'>
              <span className='text-body-xs font-bold text-text-primary'>{step.sessions.toLocaleString()}</span>
              <span className='text-caption text-text-tertiary ml-1.5'>
                {pct(step.share)}
                {prev != null && prev > 0 ? ` · ${Math.round((step.sessions / prev) * 100)}% of previous` : ""}
              </span>
            </div>
          </div>
        );
      })}
    </div>
  );
}

// Live journey rollups: funnel, entry paths, top paths, surfaces and Activity-feed usage.
export default function JourneysTab({ timeRange, useProd }) {
  const window = useMemo(() => rangeToIso(timeRange), [timeRange]);
  const { data, isLoading, isFetching, error } = useGetCustomerEngagementQuery({ start: window.start, end: window.end, useProd });

  if (isLoading) return <Loading label='Computing journeys…' />;
  if (error) {
    return (
      <EmptyState title={`Journey rollups unavailable in ${useProd ? "production" : "staging"}`}>
        {(error.data && error.data.detail) || "The Platform engagement endpoint returned an error (it needs the Sept 2026 Platform build)."}
      </EmptyState>
    );
  }
  if (!data || data.sessions === 0) {
    return (
      <EmptyState title={`No customer Hub sessions in the last ${timeRange}`}>
        Staff sessions are excluded{useProd ? "" : ", and staging traffic is almost entirely staff, so this tab is only meaningful on production"}.
      </EmptyState>
    );
  }

  const { funnel, entry_paths: entry, paths, surfaces, activity } = data;
  const top = funnel[0] ? funnel[0].sessions : 0;
  const last = funnel[funnel.length - 1];
  let dropIdx = 1;
  for (let i = 2; i < funnel.length; i += 1) {
    if (funnel[i - 1].sessions - funnel[i].sessions > funnel[dropIdx - 1].sessions - funnel[dropIdx].sessions) dropIdx = i;
  }
  const entryRows = Object.entries(entry.counts || {}).sort((a, b) => b[1] - a[1]);
  const bounce = paths.filter((p) => p.acted === 0).reduce((s, p) => s + p.sessions, 0);
  const listed = paths.reduce((s, p) => s + p.sessions, 0);

  return (
    <div className={`space-y-6 ${isFetching ? "opacity-60" : ""}`} data-testid='tab-journeys'>
      <div className='grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-3.5'>
        <StatCard label='Hub sessions' value={data.sessions.toLocaleString()} note={`last ${timeRange} · staff excluded`} />
        <StatCard
          label='Overview → action'
          value={pct(last && top ? last.sessions / top : null)}
          note={`${last ? last.sessions : 0} of ${top} overview sessions acted`}
          color='text-accent-green'
          labelColor='text-accent-green'
        />
        <StatCard
          label='Bypass rate'
          value={entry.overview_reported ? pct(entry.bypass_rate) : "n/a"}
          note={entry.overview_reported ? `${entry.total.toLocaleString()} finding views` : "needs Hub sessions on the new build"}
          color='text-severity-medium'
          labelColor='text-severity-medium'
        />
        <StatCard
          label='Ranked position clicks'
          value={(data.position_click_sessions || 0).toLocaleString()}
          note={`sessions · ${pct(top ? (data.position_click_sessions || 0) / top : null)} of overview sessions`}
          color='text-accent-purple'
          labelColor='text-accent-purple'
        />
      </div>

      <div className='grid grid-cols-1 lg:grid-cols-2 gap-6'>
        <Card className='flex flex-col justify-between'>
          <div>
            <CardHeader
              title='Engagement funnel'
              badge={`${top} sessions`}
              hint='Per session, each step requires the ones before it: loaded the ranked overview, opened a finding (any path), stayed 30 s on it, then acted (done / discard / share / assign). Dwell is measured from the navigation timeline.'
            />
            <FunnelRows funnel={funnel} />
          </div>
          {top > 0 && funnel[dropIdx] && (
            <Reading>
              biggest drop-off is "{funnel[dropIdx - 1].step}" → "{funnel[dropIdx].step}" ({funnel[dropIdx - 1].sessions} → {funnel[dropIdx].sessions}).
            </Reading>
          )}
        </Card>

        <Card className='flex flex-col justify-between'>
          <div>
            <CardHeader
              title='How findings are reached'
              badge='journey.finding_reached'
              hint='Entry path of every finding view. Bypass rate = views that did not come from the ranked overview: the number that says whether the top-10 ranker drives behaviour.'
            />
            <div className='text-center py-2'>
              <div className='text-[54px] font-extrabold text-text-primary tracking-tight'>{entry.overview_reported ? pct(entry.bypass_rate) : "n/a"}</div>
              <div className='text-body-xs font-semibold text-text-tertiary'>
                {entry.overview_reported ? `bypass rate over ${entry.total.toLocaleString()} finding views` : `${entry.total.toLocaleString()} finding views, none yet attributed to the overview`}
              </div>
            </div>
            {entry.total > 0 && (
              <div className='h-8 rounded-xl overflow-hidden flex shadow-xs my-4 font-bold text-[10px] text-white'>
                {entryRows.map(([k, n]) => (
                  <div key={k} style={{ width: `${(n / entry.total) * 100}%`, backgroundColor: ENTRY_COLORS[k] || "#6C7187" }} className='flex items-center justify-center truncate px-1' title={`${ENTRY_LABELS[k] || k}: ${n}`}>
                    {Math.round((n / entry.total) * 100) >= 12 ? `${ENTRY_LABELS[k] || k} · ${Math.round((n / entry.total) * 100)}%` : ""}
                  </div>
                ))}
              </div>
            )}
            <div className='space-y-1.5'>
              {entryRows.map(([k, n]) => (
                <div key={k} className='flex items-center justify-between text-body-xs font-semibold'>
                  <div className='flex items-center gap-2'>
                    <span className='w-2.5 h-2.5 rounded-xs' style={{ backgroundColor: ENTRY_COLORS[k] || "#6C7187" }} />
                    <span className='text-text-secondary'>{ENTRY_LABELS[k] || k}</span>
                  </div>
                  <strong>
                    {n.toLocaleString()} <span className='text-text-tertiary font-normal'>({Math.round((n / entry.total) * 100)}%)</span>
                  </strong>
                </div>
              ))}
            </div>
          </div>
          <Reading>
            {entry.overview_reported
              ? "views from the findings list and direct links are the bypasses; direct links are mostly the run-complete emails."
              : "the Hub only started reporting overview and Activity entries in Sept 2026; every view before that was logged as a direct link or the findings list. The rate appears once sessions on the new build accumulate."}
          </Reading>
        </Card>
      </div>

      <Card className='space-y-4'>
        <CardHeader title='User journeys · top paths' badge='journey.navigation' hint='The most common surface sequences per session, from the landing page onward, and how often each ends in a finding action. A single surface means the session never left it.' />
        <div className='border border-border-subtle rounded-xl overflow-hidden'>
          <table className='w-full text-left text-body-xs font-semibold'>
            <thead>
              <tr className='bg-surface-neutral/60 border-b border-border-subtle'>
                <th className='px-4 py-2.5 font-bold text-text-secondary uppercase tracking-wider'>Path</th>
                <th className='px-4 py-2.5 text-right font-bold text-text-secondary uppercase tracking-wider w-28'>Sessions</th>
                <th className='px-4 py-2.5 text-right font-bold text-text-secondary uppercase tracking-wider w-20'>%</th>
                <th className='px-4 py-2.5 text-right font-bold text-text-secondary uppercase tracking-wider w-32'>Action rate</th>
              </tr>
            </thead>
            <tbody className='divide-y divide-border-subtle'>
              {paths.map((p) => (
                <tr key={p.path} className='hover:bg-surface-neutral/30'>
                  <td className='px-4 py-2.5 text-text-primary'>{p.path.split(" → ").map((n) => SURFACE_LABELS[n] || n).join(" → ")}</td>
                  <td className='px-4 py-2.5 text-right font-mono text-text-secondary'>{p.sessions}</td>
                  <td className='px-4 py-2.5 text-right font-mono text-text-secondary'>{pct(p.share)}</td>
                  <td className={`px-4 py-2.5 text-right font-mono font-bold ${p.action_rate ? "text-accent-green" : "text-text-tertiary"}`}>{pct(p.action_rate)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <Reading>
          {bounce} of the {listed} sessions on these paths ended without a finding action; a run click leads into a finding in {pct(activity.run_click_to_finding_rate)} of run-click sessions.
        </Reading>
      </Card>

      <div className='grid grid-cols-1 lg:grid-cols-2 gap-6'>
        <Card>
          <CardHeader title='Page views by surface' badge='journey.navigation' hint='Visits per Hub surface (landing page plus every navigation), distinct users, and the median time spent before moving on (capped at 30 min).' />
          <div className='border border-border-subtle rounded-xl overflow-hidden'>
            <table className='w-full text-left text-body-xs font-semibold'>
              <thead>
                <tr className='bg-surface-neutral/60 border-b border-border-subtle'>
                  <th className='px-4 py-2 font-bold text-text-secondary uppercase'>Surface</th>
                  <th className='px-4 py-2 text-right font-bold text-text-secondary uppercase'>Views</th>
                  <th className='px-4 py-2 text-right font-bold text-text-secondary uppercase'>Users</th>
                  <th className='px-4 py-2 text-right font-bold text-text-secondary uppercase'>Median time</th>
                </tr>
              </thead>
              <tbody className='divide-y divide-border-subtle'>
                {surfaces.map((s) => (
                  <tr key={s.surface} className='hover:bg-surface-neutral/30'>
                    <td className='px-4 py-2.5 text-text-primary'>{SURFACE_LABELS[s.surface] || s.surface}</td>
                    <td className='px-4 py-2.5 text-right font-mono'>{s.views.toLocaleString()}</td>
                    <td className='px-4 py-2.5 text-right font-mono'>{s.users}</td>
                    <td className='px-4 py-2.5 text-right font-mono text-text-secondary'>{s.median_dwell_ms != null ? formatDuration(s.median_dwell_ms) : "—"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Card>

        <Card className='flex flex-col justify-between'>
          <div>
            <CardHeader title='Activity feed' badge='activity.viewed · run_clicked' hint='Is anyone using the Activity feed, and does it lead anywhere?' />
            <div className='grid grid-cols-2 gap-3 mb-4'>
              <div className='bg-white rounded-xl border border-border-subtle p-3.5 shadow-xs'>
                <p className='text-[10px] font-semibold text-text-tertiary uppercase tracking-wider mb-1'>Sessions touching Activity</p>
                <p className='text-metric text-text-primary' style={{ fontSize: "20px" }}>{pct(activity.sessions_touching_share)}</p>
                <p className='text-[9px] text-text-tertiary mt-1'>{activity.sessions_touching} of {data.sessions} sessions</p>
              </div>
              <div className='bg-white rounded-xl border border-border-subtle p-3.5 shadow-xs'>
                <p className='text-[10px] font-semibold text-text-tertiary uppercase tracking-wider mb-1'>Run click → finding</p>
                <p className='text-metric text-accent-green' style={{ fontSize: "20px" }}>{pct(activity.run_click_to_finding_rate)}</p>
                <p className='text-[9px] text-text-tertiary mt-1'>of {activity.run_click_sessions} sessions with a run click</p>
              </div>
            </div>
            <div className='space-y-1.5 text-body-xs font-semibold'>
              <div className='flex justify-between border-b pb-1'>
                <span className='text-text-secondary'>Activity views</span>
                <span className='text-text-primary font-bold'>{activity.views.toLocaleString()}</span>
              </div>
              <div className='flex justify-between border-b pb-1'>
                <span className='text-text-secondary'>Run clicks</span>
                <span className='text-text-primary font-bold'>{activity.run_clicks.toLocaleString()}</span>
              </div>
              {activity.top_agents.length > 0 && <p className='text-[10px] font-semibold text-text-tertiary uppercase tracking-wider pt-2'>Most clicked agents</p>}
              {activity.top_agents.map((a) => (
                <div key={a.agent_id} className='flex justify-between border-b pb-1 gap-3'>
                  <span className='text-text-secondary truncate' title={a.agent_id}>{a.title || a.agent_id}</span>
                  <span className='text-text-primary shrink-0'>{a.clicks.toLocaleString()}</span>
                </div>
              ))}
            </div>
          </div>
        </Card>
      </div>
    </div>
  );
}
